/**
 * Listing Utilities
 *
 * Diamond Architecture: Pure functions for listing display formatting.
 * Separated from components for reuse across ListingCard, MapPin, SearchResult, etc.
 *
 * All functions are tree-shakable and unit-testable.
 *
 * Note: UI labels (Bd, Ba, /mo) are NOT in these utils.
 * Components use translations directly for those.
 * These utils handle numeric formatting and logic only.
 */
import { displayPriceForIntent } from '@letsgethome/lib/currency';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import { basicInfoParts, formatDisplayTitle } from '@letsgethome/lib/wasm';
import {
  assetPath,
  plaintextString,
  type AssetPath,
  type Cents,
  type Locale,
  type PlaintextString,
  type S3Key,
} from '@letsgethome/types/branded';

import type { ListingCardData } from './ListingCard.types';

/**
 * Format area for display (numeric only, suffix added by component).
 *
 * @example
 * formatArea(850)  // "850"
 * formatArea(1200) // "1,200"
 */
export function formatArea(saleableArea: number): PlaintextString {
  return plaintextString(saleableArea.toLocaleString());
}

/**
 * Get the display image URL from a listing.
 * Falls back to placeholder if no images available.
 */
export function getDisplayImage(imageKeys: S3Key[]): S3Key | AssetPath {
  return imageKeys[0] ?? assetPath('/placeholder-listing.jpg');
}

/**
 * Get the price data for a listing based on the browsing intent.
 *
 * The DECISION is the STRICT Rust SSOT
 * (`domain_core::listing_display::display_price_for_intent` via the typed
 * `@letsgethome/lib/currency` shim): a price exists only when the listing is offered for the
 * browsed intent AND carries a positive price (zero is the unset sentinel).
 * `price: null` means HIDE the price element — it signals an upstream data
 * bug; the other mode's price is NEVER substituted, so the old silent
 * sale-price-in-a-rent-slot fallback is now unrepresentable.
 */
export function getListingPriceData(
  listing: Pick<ListingCardData, 'rentPriceCents' | 'salePriceCents' | 'forRent' | 'forSale'>,
  transactionType: TransactionModeValue
): { price: Cents | null; isRent: boolean } {
  const isRent = transactionType === 'rent';
  const priceCents = displayPriceForIntent(
    isRent,
    listing.forRent,
    listing.forSale,
    listing.rentPriceCents,
    listing.salePriceCents
  );
  return { price: priceCents ?? null, isRent };
}

// ----------------------------------------------------------------------------
// Display Title & Location (matches Rails LocationPresenter)
// ----------------------------------------------------------------------------

/**
 * Get the display title for a listing — the Rust SSOT chain
 * (`domain_core::listing_display::format_display_title` via `domain-wasm`):
 * building label → estate name → manual address → the locale-localized
 * "Untitled Listing". Never empty/null; the fallback words live in the SSOT.
 *
 * On a storefront card `buildingLabel` is the server's PUBLIC, estate-qualified
 * label (wire epoch 40, D9/D10), so the title reads `Bauhinia Garden Tower 2`
 * and a village card reads the village. The owner's pages pass their draft's
 * own label through the same chain — there is no second title rule.
 *
 * @example
 * getDisplayTitle({ buildingLabel: "City One Park View", estateName: "City One" }, 'en') // "City One Park View"
 * getDisplayTitle({ estateName: "Taikoo Shing" }, 'en') // "Taikoo Shing"
 * getDisplayTitle({}, 'en') // "Untitled Listing"
 */
export function getDisplayTitle(
  listing: {
    buildingLabel?: string;
    estateName?: string;
    manualAddress?: string;
  },
  locale: Locale
): string {
  return formatDisplayTitle(
    locale,
    listing.buildingLabel,
    listing.estateName,
    listing.manualAddress
  );
}

// ----------------------------------------------------------------------------
// Basic Info (matches Rails Presenter#basic_info, Mobile ListingFormatter)
// ----------------------------------------------------------------------------

/**
 * Format the basic info line: beds · baths · area
 * Returns parts for components to join with translations.
 *
 * The DECISION — which parts appear, their order, and the studio rule
 * (a STATED 0 bedrooms renders "Studio", never "0 beds") — is Rust-owned
 * (`domain_core::listing_display::basic_info_parts` via `domain-wasm`);
 * the WORDS stay in the web ui-strings catalog (components render them
 * with `t(...)`), and `formatArea` here is only presentational grouping.
 *
 * The SSOT's shape is carried THROUGH, not flattened (LET-93): `bedrooms` is
 * `null` both for an unstated count and for a studio, and `isStudio` is what
 * tells them apart. Until epoch 33 this function mapped a studio back onto
 * `bedrooms: 0`, re-creating exactly the `0`-means-studio conflation the SSOT
 * exists to remove — and every wizard listing, whose count was never stated,
 * arrived here as a manufactured `0`.
 *
 * The area is carried the same way (LET-101): `area: null` means the owner
 * never stated one, not an area of zero. It has no `isStudio`-shaped
 * companion because it needs none — there is no stated `0` area for a null
 * to be confused with.
 *
 * @example
 * getBasicInfoParts({ bedrooms: 2, bathrooms: 1, saleableArea: 850 })
 * // { bedrooms: 2, bathrooms: 1, area: "850", isStudio: false }
 * getBasicInfoParts({ saleableArea: undefined })
 * // { bedrooms: null, bathrooms: null, area: null, isStudio: false }
 * getBasicInfoParts({ bedrooms: 0 })
 * // { bedrooms: null, bathrooms: null, area: null, isStudio: true }
 * getBasicInfoParts({})
 * // { bedrooms: null, bathrooms: null, area: null, isStudio: false }
 */
export function getBasicInfoParts(listing: {
  bedrooms?: number;
  bathrooms?: number;
  saleableArea?: number;
}): {
  /** Positive stated count; `null` for a studio AND for "not stated". */
  bedrooms: number | null;
  bathrooms: number | null;
  area: string | null;
  /** The ONLY signal that a `null` bedrooms is a stated studio. */
  isStudio: boolean;
} {
  // `undefined` (every part missing) maps onto the all-null shape below.
  const parts = basicInfoParts(listing.bedrooms, listing.bathrooms, listing.saleableArea);

  return {
    bedrooms: parts?.bedrooms ?? null,
    bathrooms: parts?.bathrooms ?? null,
    area: parts?.areaSqft != null ? formatArea(parts.areaSqft) : null,
    isStudio: parts?.isStudio ?? false,
  };
}
